import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { api, formatBRL, formatDateBR } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  AlertTriangle, Wallet, Coins, Users, FileText, Layers,
  Percent, Phone, ChevronRight, Loader2, Filter, RefreshCcw, MessageSquare,
} from "lucide-react";
import SendWhatsAppModal from "../components/SendWhatsAppModal";

const BUCKETS = [
  { id: "", label: "Todos" },
  { id: "1-7", label: "1 a 7 dias" },
  { id: "8-15", label: "8 a 15 dias" },
  { id: "16-30", label: "16 a 30 dias" },
  { id: "31-60", label: "31 a 60 dias" },
  { id: "60+", label: "Acima de 60 dias" },
];

const BUCKET_CLS = {
  "1-7":   "bg-yellow-500/10  text-yellow-200  border-yellow-500/40",
  "8-15":  "bg-amber-500/15   text-amber-300   border-amber-500/50",
  "16-30": "bg-orange-500/15  text-orange-300  border-orange-500/50",
  "31-60": "bg-red-500/15     text-red-300     border-red-500/50",
  "60+":   "bg-red-800/25     text-red-200     border-red-700/60",
};

function Kpi({ label, value, accent, icon: Icon, testid, suffix }) {
  return (
    <div className="fc-card p-5 fc-card-hover" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3] font-medium">{label}</div>
        {Icon && (
          <div className={`w-8 h-8 rounded-md flex items-center justify-center border ${accent || "border-[#262626] text-[#A3A3A3]"}`}>
            <Icon size={15} strokeWidth={1.8}/>
          </div>
        )}
      </div>
      <div className="fc-kpi-value text-2xl sm:text-[26px] mt-3 leading-none">
        {value}{suffix && <span className="text-[14px] text-[#737373] ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

export default function CobrancaPage() {
  const { user } = useAuth();
  const isGestora = user?.role === "gestora";
  const [kpi, setKpi] = useState(null);
  const [aging, setAging] = useState({ count: 0, items: [] });
  const [bucket, setBucket] = useState("");
  const [loading, setLoading] = useState(true);
  const [waClient, setWaClient] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = bucket ? { bucket } : {};
      const [k, a] = await Promise.all([
        api.get("/dashboard/collections"),
        api.get("/dashboard/collections/aging", { params }),
      ]);
      setKpi(k.data);
      setAging(a.data);
    } catch (_) {
      setKpi(null);
      setAging({ count: 0, items: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bucket]);

  const counts = useMemo(() => {
    const c = { "": kpi?.parcelas_em_atraso + (kpi?.contratos_em_atraso || 0) || 0 };
    (kpi?.aging || []).forEach((b) => { c[b.bucket] = b.count; });
    return c;
  }, [kpi]);

  const whatsappLink = (phone) => {
    const digits = (phone || "").replace(/\D/g, "");
    if (!digits) return null;
    return `https://wa.me/55${digits.startsWith("55") ? digits.slice(2) : digits}`;
  };

  return (
    <Layout>
      <div className="mb-7 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Visão executiva</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Cobrança e Inadimplência</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Carteira em atraso (contratos normais + parcelados/acordos)</p>
        </div>
        <button onClick={load} className="fc-btn-ghost flex items-center gap-2" data-testid="reload-collections">
          <RefreshCcw size={13} /> Atualizar
        </button>
      </div>

      {loading && !kpi ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[1,2,3,4,5,6,7,8].map(i=>(<div key={i} className="fc-card h-28 shimmer"/>))}
        </div>
      ) : kpi && (
        <>
          {/* KPIs principais */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi label="Capital em atraso" value={formatBRL(kpi.capital_em_atraso)} icon={Wallet} accent="border-red-500/40 text-red-300" testid="kpi-capital-atraso"/>
            <Kpi label="Juros em atraso" value={formatBRL(kpi.juros_em_atraso)} icon={Coins} accent="border-red-500/40 text-red-300" testid="kpi-juros-atraso"/>
            <Kpi label="Total em atraso" value={formatBRL(kpi.total_em_atraso)} icon={AlertTriangle} accent="border-red-500/40 text-red-300" testid="kpi-total-atraso"/>
            {!isGestora && (
              <Kpi label="Taxa de inadimplência" value={`${kpi.taxa_inadimplencia.toFixed(2)}%`} icon={Percent} accent="border-yellow-500/40 text-yellow-300" testid="kpi-taxa-inadimplencia"/>
            )}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
            <Kpi label="Clientes em atraso" value={kpi.clientes_em_atraso} icon={Users} testid="kpi-clientes-atraso"/>
            <Kpi label="Contratos em atraso" value={kpi.contratos_em_atraso} icon={FileText} testid="kpi-contratos-atraso"/>
            <Kpi label="Parcelas em atraso" value={kpi.parcelas_em_atraso} icon={Layers} testid="kpi-parcelas-atraso"/>
            {!isGestora && (
              <Kpi label="Capital na rua" value={formatBRL(kpi.capital_na_rua)} icon={Wallet} testid="kpi-capital-na-rua-cob"/>
            )}
          </div>

          {/* Aging breakdown — minicards */}
          <div className="mb-5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#737373] mb-2 flex items-center gap-1.5">
              <Filter size={11}/> Distribuição por dias de atraso
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
              {kpi.aging.map((a) => (
                <button
                  key={a.bucket}
                  onClick={() => setBucket(bucket === a.bucket ? "" : a.bucket)}
                  className={`text-left p-3 rounded-md border transition-colors ${
                    bucket === a.bucket
                      ? "border-[#D4AF37]/50 bg-[#D4AF37]/5"
                      : "border-[#1f1f1f] bg-[#0a0a0a] hover:border-[#262626]"
                  }`}
                  data-testid={`aging-bucket-${a.bucket}`}
                >
                  <div className={`text-[10px] font-semibold uppercase tracking-wider inline-block px-1.5 py-0.5 rounded border ${BUCKET_CLS[a.bucket] || "border-[#262626] text-[#A3A3A3]"}`}>
                    {a.bucket} dias
                  </div>
                  <div className="font-display text-lg font-bold mt-2 text-white">{a.count}</div>
                  <div className="text-[10.5px] text-[#737373] mt-0.5">parcela(s) · {a.clientes} cliente(s)</div>
                  <div className="text-[12px] text-[#D4AF37] font-mono mt-1">{formatBRL(a.total)}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Filtro de bucket + tabela */}
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex flex-wrap gap-1.5" data-testid="bucket-tabs">
              {BUCKETS.map((b) => (
                <button
                  key={b.id}
                  onClick={() => setBucket(b.id)}
                  data-testid={`tab-bucket-${b.id || "all"}`}
                  className={`px-3 py-1.5 rounded-md text-[12.5px] border transition-colors ${
                    bucket === b.id
                      ? "bg-[#D4AF37]/10 border-[#D4AF37]/40 text-[#D4AF37]"
                      : "bg-[#0a0a0a] border-[#1f1f1f] text-[#A3A3A3] hover:text-white"
                  }`}
                >
                  {b.label}
                  {b.id && counts[b.id] != null && (
                    <span className="ml-1 text-[10px] text-[#737373]">({counts[b.id] || 0})</span>
                  )}
                </button>
              ))}
            </div>
            <div className="text-[12px] text-[#737373]">
              {aging.count} cliente(s){bucket && ` no bucket ${bucket} dias`}
            </div>
          </div>

          <div className="fc-card overflow-hidden">
            {loading ? (
              <div className="p-5 flex items-center gap-2 text-[#737373] text-sm">
                <Loader2 size={14} className="animate-spin"/> Atualizando...
              </div>
            ) : aging.items.length === 0 ? (
              <div className="p-10 text-center text-[12.5px] text-[#737373]" data-testid="aging-empty">
                Nenhum cliente em atraso neste filtro. 🎉
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]" data-testid="aging-table">
                  <thead>
                    <tr className="border-b border-[#1f1f1f] text-[10px] uppercase tracking-wider text-[#737373]">
                      <th className="text-left py-2.5 px-3">Cliente</th>
                      <th className="text-left py-2.5">Indicador</th>
                      <th className="text-left py-2.5">Sócio</th>
                      <th className="text-right py-2.5">Capital</th>
                      <th className="text-right py-2.5">Juros</th>
                      <th className="text-right py-2.5">Total</th>
                      <th className="text-center py-2.5">Atraso</th>
                      <th className="text-center py-2.5">Contato</th>
                      <th className="text-center py-2.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aging.items.map((r) => {
                      const wa = whatsappLink(r.whatsapp);
                      return (
                        <tr key={r.client_id} className="border-b border-[#141414] hover:bg-[#0d0d0d]" data-testid={`aging-row-${r.client_id}`}>
                          <td className="py-2.5 px-3">
                            <a href={`/clientes/${r.client_id}`} className="text-white hover:text-[#D4AF37] font-medium">{r.full_name}</a>
                            <div className="text-[10.5px] text-[#737373] mt-0.5">{r.items.length} parcela(s) em atraso</div>
                          </td>
                          <td className="py-2.5 text-[#A3A3A3]">{r.indicator_name}</td>
                          <td className="py-2.5 text-[#A3A3A3]">{r.socio_name}</td>
                          <td className="py-2.5 text-right font-mono text-[#D4D4D4]">{formatBRL(r.capital)}</td>
                          <td className="py-2.5 text-right font-mono text-[#D4D4D4]">{formatBRL(r.juros)}</td>
                          <td className="py-2.5 text-right font-mono text-[#D4AF37] font-semibold">{formatBRL(r.total)}</td>
                          <td className="py-2.5 text-center">
                            <span className={`badge-base border text-[10px] ${BUCKET_CLS[r.bucket] || "border-[#262626] text-[#A3A3A3]"}`}>
                              {r.max_days}d
                            </span>
                          </td>
                          <td className="py-2.5 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              {wa && (
                                <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200" data-testid={`whatsapp-${r.client_id}`} title={r.whatsapp}>
                                  <Phone size={11}/>
                                </a>
                              )}
                              <button
                                onClick={() => setWaClient({ id: r.client_id, full_name: r.full_name, whatsapp: r.whatsapp })}
                                className="inline-flex items-center gap-1 text-[11px] text-[#D4AF37] hover:text-[#E8C547]"
                                data-testid={`send-cobranca-${r.client_id}`}
                                title="Enviar cobrança via WhatsApp"
                              >
                                <MessageSquare size={12}/>
                              </button>
                              {!wa && !r.whatsapp && <span className="text-[#737373]">—</span>}
                            </div>
                          </td>
                          <td className="py-2.5 text-center">
                            {r.is_blocked ? (
                              <span className="badge-base text-[10px] border bg-red-500/15 text-red-300 border-red-500/40">Bloqueado</span>
                            ) : (
                              <span className="text-[11px] text-[#A3A3A3]">{r.status}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      <SendWhatsAppModal
        open={!!waClient}
        onClose={() => setWaClient(null)}
        clients={waClient ? [waClient] : []}
        defaultCategory="cobranca"
      />
    </Layout>
  );
}
